export interface ISuperPacmanInstallment {
  financeProduct: ISuperPacmanFinanceProduct;
  cumulativeBalance: ISuperPacmanCumulativeBalance;
  validThrough: ISuperPacmanValidThrough;
  daysToAmount: ISuperPacmanDaysToAmountItem[];
}

export const FinanceProductTypes = {
  FEv1: "FEv1",
  FEv2: "FEv2",
  JointLoanLine: "JointLoanLine",
};

export const CustomerNationalIdTypes = {
  ResidencePermit: "Residence Permit",
  NationalId: "National ID",
};

export interface ISuperPacmanFinanceProduct {
  financeProductType: keyof typeof FinanceProductTypes | "";
  financeProductId: number | null;
  financeOrderId?: number | null; // Set as null because a client does not necessarily have finance order (phone finance)
  creditLineId?: number | null; // Set as null because a client does not necessarily have a credit line
  country: string;
  currency: string;
  customerName: string;
  customerUserId?: number;
  customerNationalIdNumber?: string;
  customerNationalIdType?: string;
  customerNationalIdCountry?: string;
  customerEmail?: string;
  customerPhoneNumber?: string;
  deviceTag: string;
  deviceModel: string;
  deviceImei: string;
  financeOrderPayoffAmountIncludingCumulativeBalance?: string | null; // Set as null because it is not clear in the requirements what this is for.
  creditLinePayoffAmount?: string | null; // Set as null because if a client only has a finance order, they will not have any credit line amounts.
  minimumPaymentAmount?: string | null; // Set as null because we might just get the minimum payment amount from daysToAmount.
  fullPayoffAmount: string;
  foMinPaymentWithCbPlusCLPayoffAmount?: string | null; // Set as null to prevent breaking something in case this gets deployed early.
  subProductVersion?: string; // field used for Brazil, indicates if customer is under subscription or other product. Sample values: Subscription, BMP
  payToAccount?: string; // account where to send the money, currently used only for ASAAS
  customerFirstName?: string;
  customerLastName?: string;
  customerDateOfBirth?: string;
  customerZipCode?: string;
  financeProviderId?: number;
  deviceId?: number;
}

export interface ISuperPacmanCumulativeBalance {
  cumulativeBalanceAmount?: string | null; // Set as null because we don't know when it's going to be implemented.
  cumulativeBalanceEnabled?: boolean | null; // Set as null to prevent breaking something in case this gets deployed early.
  enabled?: boolean | null;
}

export interface ISuperPacmanValidThrough {
  currentValidThrough: string;
}

export interface ISuperPacmanDaysToAmountItem {
  daysPaid: number;
  dateUnlockedUntil: number;
  amount: string;
}

export interface ISuperPacmanFetchInstallmentRequest {
  deviceTag: string;
  optionName: string;
}

export interface ISuperPacmanInstallmentItem {
  label: string;
  subtitle?: string;
  amount: string;
  currency?: string;
  originalLabel: string;
}

export interface ISuperPacmanReferenceRequest {
  financeProductType: string;
  financeProductId: number;
  countryCode: string;
  currency: string;
  amount: string;
  paymentType: string;
  paymentSubType: string | null;
  phoneNumber: string | null;
  customerName?: string;
  customerNationalId?: string;
  payToAccount?: string; // account where to send the money, currently used only in Brazil
  deviceTag?: string;
}

export interface ISuperPacmanWompiCreateReferenceRequest extends ISuperPacmanReferenceRequest {
  acceptanceToken: string;
  deviceTag: string;
  payer: {
    email: string;
    name: string;
    document: string;
    documentType?: string;
    // Daviplata wallet document typed by the customer. Kept separate from
    // document/documentType so the Finance Engine identity is never overridden.
    userInputDocument?: string;
    userInputDocumentType?: string;
  };
  requestUrl?: string;
  financialInstitutionCode?: string;
  customerUserId?: number;
}

export interface ISuperPacmanMonnetCreateReferenceRequest extends ISuperPacmanReferenceRequest {
  // interface for Monnet reference creation
  payinCustomerEmail?: string;
  payinCustomerName?: string;
  payinCustomerLastname?: string;
  payinCustomerDocument?: string;
  payinCustomerTypeDocument?: string;
}

export interface IBackendErrors {
  type: string;
  messages: {
    [key: string]: string[];
  };
}

export interface ISuperPacmanReferenceResponse {
  financeProductType: keyof typeof FinanceProductTypes | "";
  financeProductId: number | null;
  country: string;
  currency: string;
  amount: string;
  paymentType: string;
  paymentSubType: string | null;
  paymentReferenceNumber1: string;
  paymentReferenceNumber2: string | null;
  paymentReferenceExpiration: number | undefined | null;
  validThrough: string | undefined;
  extraFields?: {
    paymentIntentionIdentifier: string;
  };
  errorDetails?: IBackendErrors;
}

export interface ISuperPacmanReferenceRedirectResponse {
  financeProductId: number | null;
  amount: string;
  paymentProvider: string;
  redirectUrl?: string;
  paymentId?: string;
  reference: string;
  validThrough: string;
  extraFields?: {
    paymentIntentionIdentifier: string;
  };
  errorDetails?: IBackendErrors;
}

export interface ISuperPacmanASAASCreateReferenceResponse {
  // interface for handling response from reference creation in ASAAS (Pix and Boleto)
  redirectUrl: string;
  referenceCode: string;
  transactionId: string;
  expiryTime: string;
  pixPayload?: string;
  pixEncodedImage?: string;
  pixExpirationDate?: string;
  errorDetails?: string;
}

export interface ISuperPacmanKasnetQRCreateReferenceResponse {
  hashQr: string;
  idQr: string;
  transactionId: string;
  errorDetails?: string;
}

export interface ISuperPacmanVirtualAccountNumberRequest {
  financeProductType: string;
  financeProductId: number;
}

export interface ISuperPacmanVirtualAccountNumberResponse {
  virtualAccountNumber: string;
  integrationType: string;
  country: string;
  status: string;
  applicationId: number;
  applicationType: string;
  financeProductType: string;
  financeProductId: number | null;
  errorDetails?: string;
}

export interface IXenditReferenceRequest {
  country: string;
  currency: string;
  request_amount: string;
  channel_code: string;
  device_tag: string;
  financial_product_id: string | number;
  finance_engine_version: string;
  customer: {
    user_id: number | string;
    name: string;
    first_name: string;
    last_name: string;
    phone_number: string;
    email?: string;
  };
}

export interface IXenditReferenceResponse {
  actionType?: string;
  paymentRequestId?: string;
  referenceId?: string;
  vaNumber?: string;
  qrString?: string;
  paymentCode?: string;
  // Populated for e-wallet channels (GCash / Maya): the hosted authorization
  // page the customer's browser must be sent to.
  redirectUrl?: string;
  expiresAt?: string;
  errorDetails?: IBackendErrors;
}
